/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography
} from '@mui/material';
import { format } from 'date-fns';
import axios from 'axios';

const ProjectRenewal = ({ projectId, onClose, open }) => {
  const [renewalHistory, setRenewalHistory] = useState([]);
  const [renewalCount, setRenewalCount] = useState(0);
  const [lastRenewalDate, setLastRenewalDate] = useState(null);
  const [newEndDate, setNewEndDate] = useState('');
  const [renewalReason, setRenewalReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && projectId) {
      fetchRenewalHistory();
    }
  }, [open, projectId]);

  const fetchRenewalHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/applications/${projectId}/renewal-history`);
      setRenewalHistory(response.data.renewal_history);
      setRenewalCount(response.data.renewal_count);
      setLastRenewalDate(response.data.last_renewal_date);
    } catch (err) {
      setError('Failed to fetch renewal history');
      console.error('Error fetching renewal history:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRenew = async () => {
    try {
      await axios.post(`/api/applications/${projectId}/renew`, {
        new_end_date: newEndDate,
        renewal_reason: renewalReason
      });
      
      // Refresh renewal history
      await fetchRenewalHistory();
      
      // Clear form
      setNewEndDate('');
      setRenewalReason('');
      
    } catch (err) {
      setError('Failed to renew project');
      console.error('Error renewing project:', err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Project Renewal Management</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        
        <Typography variant="h6" sx={{ mb: 2 }}>
          Total Renewals: {renewalCount}
          {lastRenewalDate && (
            <Typography variant="body2" color="text.secondary">
              Last renewed on: {format(new Date(lastRenewalDate), 'PPP')}
            </Typography>
          )}
        </Typography>

        <Typography variant="h6" sx={{ mb: 2 }}>New Renewal</Typography>
        <TextField
          label="New End Date"
          type="date"
          value={newEndDate}
          onChange={(e) => setNewEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Renewal Reason"
          value={renewalReason}
          onChange={(e) => setRenewalReason(e.target.value)}
          multiline
          rows={3}
          fullWidth
          sx={{ mb: 3 }}
        />

        <Typography variant="h6" sx={{ mb: 2 }}>Renewal History</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Renewal Date</TableCell>
                <TableCell>Renewed By</TableCell>
                <TableCell>Previous End Date</TableCell>
                <TableCell>New End Date</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renewalHistory.map((renewal, index) => (
                <TableRow key={index}>
                  <TableCell>{format(new Date(renewal.renewed_at), 'PPP')}</TableCell>
                  <TableCell>
                    {renewal.renewed_by.first_name} {renewal.renewed_by.last_name}
                  </TableCell>
                  <TableCell>{format(new Date(renewal.previous_end_date), 'PPP')}</TableCell>
                  <TableCell>{format(new Date(renewal.new_end_date), 'PPP')}</TableCell>
                  <TableCell>{renewal.renewal_reason}</TableCell>
                </TableRow>
              ))}
              {renewalHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No renewal history available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button 
          onClick={handleRenew}
          variant="contained" 
          color="primary"
          disabled={!newEndDate || !renewalReason}
        >
          Renew Project
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectRenewal; 